import analyticsService from '@lib/analytics/AnalyticsService'
import { useEffect, useState } from 'react'

export type CookieBot = {
    consent: {
        preferences: boolean
        statistics: boolean
        marketing: boolean
    }
    doNotTrack: boolean
    hasResponse: boolean
    hide(): void
    renew(): void
    runScripts(): void
    show(): void
    submitCustomConsent(
        optinPreferences?: boolean,
        optinStatistics?: boolean,
        optinMarketing?: boolean,
    ): void
}

export const useCookieConsent = () => {
    const [, setConsent] = useState<CookieBot['consent'] | undefined>()

    const handleConsentChange = async () => {
        const newConsent = { ...window.Cookiebot?.consent }
        setConsent(newConsent)

        const hasAnalyticsConsent =
            newConsent?.statistics === true &&
            newConsent?.marketing === true &&
            window?.Cookiebot?.doNotTrack === false

        // Push consent event to dataLayer - GTM will handle filtering based on consent
        window?.dataLayer?.push({
            event: 'cookie_consent_updated',
            consent: {
                statistics: newConsent?.statistics,
                marketing: newConsent?.marketing,
                preferences: newConsent?.preferences,
            },
            hasAnalyticsConsent,
        })

        // Process any pending analytics events if consent is granted
        if (hasAnalyticsConsent) {
            await analyticsService.processPendingEvents()
        }
    }

    useEffect(() => {
        // add listeners for consent changes
        window.addEventListener('CookiebotOnAccept', handleConsentChange)
        window.addEventListener('CookiebotOnDecline', handleConsentChange)

        // if consent already exists on first load, call the callback to handle it
        // this handles the case where the event listeners are added after
        // the consent script has already been loaded
        if (window?.Cookiebot?.consent) {
            handleConsentChange()
        }

        return () => {
            window.removeEventListener('CookiebotOnAccept', handleConsentChange)
            window.removeEventListener(
                'CookiebotOnDecline',
                handleConsentChange,
            )
        }
    }, [])
}

// Utility function to check if user has given consent for analytics tracking
export const hasCookieConsent = () => {
    const consent = window.Cookiebot?.consent
    return (
        consent?.statistics === true &&
        consent?.marketing === true &&
        window.Cookiebot?.doNotTrack === false
    )
}
